/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Typography } from "../..";
import OptionWrapper from "../../others/option-wrapper";
import PropTypes from 'prop-types';
import { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import { flexRow, parseMinHeight, typographyTypes } from "../../../styles/general";
import { useTheme } from "../../../theme";
import { classNames } from "../../../utils";
const {
  colors: {
    system: {
      disabled: systemDisabled
    },
    text: {
      normal_label
    }
  },
  spacing
} = useTheme();
const Label = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  children,
  className,
  color,
  disabled,
  hoverTooltip,
  id,
  lineClamp,
  readOnly,
  required,
  type,
  ...props
}, reference) => {
  if (!reference) reference = useRef(null);
  const ref = useRef(null);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  return useMemo(() => jsx("div", {
    css: LabelContainerCSS,
    className: 'DGN-UI-Label-Container'
  }, jsx(Typography, {
    css: LabelRootCSS,
    ref: ref,
    id: id,
    fullWidth: false,
    mapping: 'label',
    color: readOnly || disabled ? systemDisabled : color,
    lineClamp: lineClamp,
    hoverTooltip: hoverTooltip,
    type: type,
    className: classNames('DGN-UI-Label', className, readOnly && 'read-only', disabled && 'disabled'),
    ...props
  }, children), required && jsx(Typography, {
    type: type,
    color: 'danger'
  }, "*")), [children, className, color, disabled, hoverTooltip, id, lineClamp, readOnly, required, type, props]);
}));
const LabelContainerCSS = css`
    ${flexRow};
    & + .DGN-UI-InputBase.outlined {
        margin-top: ${spacing([0.5])};
    }
`;
const LabelRootCSS = css`
    ${parseMinHeight(16)};
    margin: 0;
    word-break: break-all;
`;
Label.defaultProps = {
  children: '',
  className: '',
  color: normal_label,
  disabled: false,
  lineClamp: 1,
  readOnly: false,
  required: false,
  type: 'h6'
};
Label.propTypes = {
  /** The content of the component. */
  children: PropTypes.node,
  /** Class for component. */
  className: PropTypes.string,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** If `true`, the component is readonly. */
  readOnly: PropTypes.bool,
  /** If `true`, the label will indicate that the input is required. */
  required: PropTypes.bool,
  /** Consult [Typography's](https://core.diginet.com.vn/ui/?path=/docs/typography) documents. */
  color: PropTypes.string,
  /** Consult [Typography's](https://core.diginet.com.vn/ui/?path=/docs/typography) documents. */
  lineClamp: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
  /** Consult [Typography's](https://core.diginet.com.vn/ui/?path=/docs/typography) documents. */
  hoverTooltip: PropTypes.bool,
  /** Consult [Typography's](https://core.diginet.com.vn/ui/?path=/docs/typography) documents. */
  type: PropTypes.oneOf(typographyTypes)
};
export { Label };
export default OptionWrapper(Label);